import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radar, MessageSquarePlus, ExternalLink, RefreshCw } from "lucide-react";
import { fetchFeaturedVoices } from "@/lib/api";
import { SocialChips } from "@/components/atfq/SocialChips";
import { VoteButton } from "@/components/atfq/VoteButton";
import { ASK_GUEST_EVENT } from "@/components/atfq/askGuest";

const nominate = (v) => {
  window.dispatchEvent(new CustomEvent(ASK_GUEST_EVENT, { detail: v }));
  document.getElementById("atfq-ask")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const fmtDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });

const VoiceCard = ({ v }) => (
  <article data-testid={`atfq-voice-${v.pillar.toLowerCase()}`} className="border-t border-slate-200 pt-6 flex flex-col">
    <div className="text-xs tracking-tight text-[var(--accent)] mb-3">{v.pillar}</div>
    <h4 className="font-display text-2xl text-[#0A192F] leading-tight tracking-tight">{v.name}</h4>
    <p className="mt-1 text-sm text-slate-600">
      {v.role}{v.affiliation ? ` · ${v.affiliation}` : ""}
    </p>
    <p className="mt-3 text-sm text-slate-700 leading-relaxed line-clamp-5">{v.blurb}</p>
    {v.question && (
      <p className="mt-3 text-sm text-[#0A192F] italic border-l-2 border-[var(--accent)] pl-3">
        &ldquo;{v.question}&rdquo;
      </p>
    )}
    <SocialChips socials={v.socials} className="mt-4" />
    <div className="mt-5 flex flex-wrap items-center gap-3">
      <VoteButton voice={v} />
      <button
        type="button"
        data-testid="atfq-voice-nominate"
        onClick={() => nominate(v)}
        className="inline-flex items-center gap-2 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-white px-3 py-2 text-xs font-medium transition-colors"
      >
        <MessageSquarePlus className="w-3.5 h-3.5" /> What should Vic ask?
      </button>
      {v.sources?.[0] && (
        <a href={v.sources[0].url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[var(--accent)]">
          Source <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  </article>
);

export const FeaturedVoices = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchFeaturedVoices().then(setData).catch(() => setData({ voices: [] }));
  }, []);

  if (!data?.voices?.length) return null;

  return (
    <div id="atfq-voices" data-testid="atfq-featured-voices" className="mt-20 md:mt-24 border-t border-slate-200 pt-16">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-2xl">
          <div className="text-xs tracking-tight text-[var(--accent)] mb-3 flex items-center gap-2">
            <Radar className="w-3.5 h-3.5" /> Voices We&apos;re Watching
          </div>
          <h3 className="font-display text-3xl md:text-5xl text-[#0A192F] leading-tight tracking-tight">
            Who we want on the mic next — and why.
          </h3>
        </div>
        <div data-testid="atfq-voices-rotation" className="flex flex-col items-end gap-2 text-xs text-slate-500">
          <span className="flex items-center gap-2">
            <RefreshCw className="w-3.5 h-3.5 text-[var(--accent)]" />
            Rotates {data.rotates_on} · next {fmtDate(data.next_rotation)}
          </span>
          <Link to="/voices" data-testid="atfq-voices-archive-link" className="text-[var(--accent)] hover:text-[#0A192F] underline underline-offset-4">
            Browse every voice we&apos;ve watched →
          </Link>
        </div>
      </div>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {data.voices.map((v) => <VoiceCard key={v.id} v={v} />)}
      </div>
    </div>
  );
};

export default FeaturedVoices;
